import { useState, useEffect, useRef } from 'react'
import { Mic, MoreVertical, Phone, Video } from 'lucide-react'

interface WhatsAppMockupProps {
    onClose?: () => void
}

const WhatsAppMockup = ({ onClose }: WhatsAppMockupProps) => {
    const [text, setText] = useState('')
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const dragStart = useRef({ x: 0, y: 0 })

    const fullText = "Olá! Sou seu assistente de inteligência artificial desenvolvido pela AscConnecta. Consulto diretrizes internas contratuais concernentes à sua empresa e posso direcionar seus clientes com soluções otimizadas de atendimento ao cliente especializado, com acesso aos dados que poderei fornecer de maneira automática sem assistência humana para que façam a solicitação."

    useEffect(() => {
        let currentIndex = 0
        const intervalId = setInterval(() => {
            if (currentIndex <= fullText.length) {
                setText(fullText.slice(0, currentIndex))
                currentIndex++
            } else {
                clearInterval(intervalId)
            }
        }, 40)

        return () => clearInterval(intervalId)
    }, [])

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true)
        dragStart.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        }
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return
        setPosition({
            x: e.clientX - dragStart.current.x,
            y: e.clientY - dragStart.current.y
        })
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    // Touch handlers
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length !== 1) return
        setIsDragging(true)
        const touch = e.touches[0]
        dragStart.current = {
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        }
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return
        const touch = e.touches[0]
        setPosition({
            x: touch.clientX - dragStart.current.x,
            y: touch.clientY - dragStart.current.y
        })
    }

    const handleTouchEnd = () => {
        setIsDragging(false)
    }

    // Adicionar ouvinte global de mouse up para lidar com arrastar fora do componente
    useEffect(() => {
        const handleGlobalMouseUp = () => setIsDragging(false)
        /* 
           Note: Global touch end is tricky because touchmove is usually on the element.
           If dragging goes off element, touchmove might stop firing if element isn't capturing.
           But for simple cases, element-level events are often enough or window-level need non-passive.
           For now we keep simple component-level touch move.
        */
        window.addEventListener('mouseup', handleGlobalMouseUp)
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
    }, [])

    const [isClosing, setIsClosing] = useState(false)

    const handleClose = () => {
        setIsClosing(true)
        setTimeout(() => {
            if (onClose) onClose()
        }, 300)
    }

    const chatContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
        }
    }, [text])

    const [stylePos, setStylePos] = useState({ bottom: '80px', right: '80px' });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setStylePos({ bottom: '20px', right: '20px' });
            } else {
                setStylePos({ bottom: '80px', right: '80px' });
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            className={`absolute w-[130px] md:w-[240px] h-[185px] md:h-[340px] bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-2xl overflow-hidden flex flex-col z-[10005] font-sans border-2 border-purple-200/50 transition-all duration-300 ease-in-out ${isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                }`}
            style={{
                bottom: stylePos.bottom,
                right: stylePos.right,
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging ? 'grabbing' : 'auto'
            }}
            onMouseMove={handleMouseMove}
        >
            {/* Cabeçalho - Área Arrastável */}
            <div
                className="bg-gradient-to-r from-purple-600 to-blue-600 p-1.5 md:p-2.5 flex items-center justify-between text-white shadow-md z-10 cursor-grab active:cursor-grabbing select-none"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="flex items-center gap-2 pointer-events-none">
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-white/20 flex items-center justify-center text-xs md:text-base">🤖</div>
                    <div className="flex flex-col">
                        <span className="font-semibold text-[10px] md:text-sm leading-tight">AscFlow AI</span>
                        <span className="text-[8px] md:text-[10px] text-white/80">online</span>
                    </div>
                </div>
                <div className="flex gap-3 text-white/90 pointer-events-none">
                    <Video size={16} />
                    <Phone size={16} />
                    <MoreVertical size={16} />
                </div>
                <button
                    onClick={handleClose}
                    className="ml-2 pointer-events-auto hover:bg-white/20 rounded p-1 transition-colors"
                    aria-label="Fechar"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            {/* Área de Chat */}
            <div
                ref={chatContainerRef}
                className="flex-1 p-4 overflow-y-auto relative"
                style={{
                    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '400px'
                }}
            >
                <div className="bg-white p-2 md:p-3 rounded-lg rounded-tl-none shadow-sm max-w-[90%] self-start text-[9px] md:text-sm text-[#111b21] relative leading-relaxed">
                    {text}
                    <span className="inline-block w-[2px] h-3 md:h-4 bg-[#008069] ml-1 animate-pulse align-middle"></span>
                    <span className="text-[8px] md:text-[10px] text-gray-500 block text-right mt-1">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {/* Cauda do balão */}
                    <div className="absolute top-0 left-[-8px] w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent"></div>
                </div>
            </div>

            {/* Área de Entrada */}
            <div className="bg-[#F0F2F5] p-1 md:p-2 flex items-center gap-1 md:gap-2">
                <div className="bg-white rounded-full flex-1 px-2 py-1 md:px-4 md:py-2 text-[8px] md:text-sm text-gray-500 shadow-sm flex items-center justify-between border border-gray-200">
                    <span>Mensagem</span>
                </div>
                <div className="w-6 h-6 md:w-10 md:h-10 bg-[#008069] rounded-full flex items-center justify-center text-white shadow-sm hover:bg-[#006d59] transition-colors cursor-pointer">
                    <Mic className="w-3 h-3 md:w-5 md:h-5" />
                </div>
            </div>
        </div >
    )
}

export default WhatsAppMockup
